'use client'

import React, { useContext } from 'react'
import { Block, Confirm, Loading, Notify } from 'notiflix'
import { colors } from '../styleGuide'

interface IContextProps {
  showLoader: (ref?: any) => void
  hideLoader: (ref?: any) => void
  showPageLoader: () => void
  hidePageLoader: () => void
  showSuccess: (message?: string) => void
  showFailure: (message?: string) => void
  showInfo: (message?: string) => void
  showWarning: (message?: string) => void
  removeNotification: () => void
  pixelConfirm: (opt: any) => void
  pixelPrompt: (opt: any) => void
  bannerInfo: IBannerInfo | null
  showPixelBanner: (banner: IBannerInfo) => void
}

interface IBannerInfo {
  label: any
  type: 'info' | 'warning' | 'error' | 'primary'
  isDismissible: boolean
  icon: string | any
  dismissAfter?: number
  onDismiss?: () => void
  isShowBanner?: boolean
}

// Create the context with actual function implementations as defaults
export const PixelFactoryContext = React.createContext<IContextProps>({
  showLoader: (ref?: any) => console.log('Default showLoader called', ref),
  hideLoader: (ref?: any) => console.log('Default hideLoader called', ref),
  showPageLoader: () => console.log('Default showPageLoader called'),
  hidePageLoader: () => console.log('Default hidePageLoader called'),
  showSuccess: (message = '') => console.log('Default showSuccess:', message),
  showFailure: (message = '') => console.error('Default showFailure:', message),
  showInfo: (message = '') => console.log('Default showInfo:', message),
  showWarning: (message = '') => console.warn('Default showWarning:', message),
  removeNotification: () => console.log('Default removeNotification called'),
  pixelConfirm: (opt: any) => {
    const result = window.confirm(opt?.message || 'Are you sure?')
    if (result && opt?.onConfirm) opt.onConfirm()
    else if (!result && opt?.onDecline) opt.onDecline()
  },
  pixelPrompt: (opt: any) => {
    const result = window.prompt(opt?.message || 'Enter value:')
    if (result !== null && opt?.onConfirm) opt.onConfirm(result)
    else if (result === null && opt?.onDecline) opt.onDecline(result)
  },
  bannerInfo: null,
  showPixelBanner: (banner: IBannerInfo) => {
    console.log('Default showPixelBanner called', banner)
  },
})

export const PixelFactory: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [bannerInfo, setBannerInfo] = React.useState<IBannerInfo | null>(null)
  const [isNotiflixReady, setIsNotiflixReady] = React.useState(false)

  // Initialize Notiflix
  React.useEffect(() => {
    const initializeNotiflix = async () => {
      try {
        // Block configuration
        Block.init({
          querySelectorLimit: 200,
          className: 'notiflix-block',
          position: 'absolute',
          zindex: 1000,
          backgroundColor: 'rgba(255,255,255,0.3)',
          rtl: false,
          fontFamily: 'Poppins',
          cssAnimation: true,
          cssAnimationDuration: 300,
          svgSize: '45px',
          svgColor: colors.factoryGray,
          messageFontSize: '14px',
          messageMaxLength: 34,
          messageColor: colors.factoryGray,
        })

        // Notify configuration
        Notify.init({
          position: 'right-top',
          opacity: 1,
          borderRadius: '5px',
          messageMaxLength: 5000,
          plainText: true,
          showOnlyTheLastOne: true,
          clickToClose: true,
          pauseOnHover: false,
          zindex: 99999999999,
          fontFamily: 'Poppins',
          useIcon: false,
          success: {
            background: colors.factoryGreen,
            textColor: colors.white,
            childClassName: 'notiflix-notify-success',
            notiflixIconColor: 'rgba(0,0,0,0.2)',
            fontAwesomeClassName: 'fas fa-check-circle',
            fontAwesomeIconColor: 'rgba(0,0,0,0.2)',
            backOverlayColor: 'rgba(50,198,130,0.2)',
          },
          failure: {
            background: colors.red,
            textColor: colors.white,
            childClassName: 'notiflix-notify-failure',
            notiflixIconColor: 'rgba(0,0,0,0.2)',
            fontAwesomeClassName: 'fas fa-times-circle',
            fontAwesomeIconColor: 'rgba(0,0,0,0.2)',
            backOverlayColor: 'rgba(255,85,73,0.2)',
          },
          warning: {
            background: colors.orange,
            textColor: colors.white,
            childClassName: 'notiflix-notify-warning',
            notiflixIconColor: 'rgba(0,0,0,0.2)',
            fontAwesomeClassName: 'fas fa-exclamation-circle',
            fontAwesomeIconColor: 'rgba(0,0,0,0.2)',
            backOverlayColor: 'rgba(238,191,49,0.2)',
          },
          info: {
            background: colors.blue,
            textColor: colors.white,
            childClassName: 'notiflix-notify-info',
            notiflixIconColor: 'rgba(0,0,0,0.2)',
            fontAwesomeClassName: 'fas fa-info-circle',
            fontAwesomeIconColor: 'rgba(0,0,0,0.2)',
            backOverlayColor: 'rgba(38,192,211,0.2)',
          },
        })

        // Loading configuration
        Loading.init({
          className: 'notiflix-loading',
          zindex: 99999999999999,
          backgroundColor: colors.spinnerBg,
          rtl: false,
          fontFamily: 'Poppins',
          cssAnimation: true,
          cssAnimationDuration: 400,
          clickToClose: false,
          customSvgUrl: null,
          customSvgCode: null,
          svgSize: '80px',
          svgColor: colors.primary,
          messageID: 'NotiflixLoadingMessage',
          messageFontSize: '15px',
          messageMaxLength: 34,
          messageColor: colors.factoryGrayLight,
        })

        // Confirm configuration
        Confirm.init({
          className: 'notiflix-confirm',
          width: '300px',
          zindex: 99999999999998,
          position: 'center',
          distance: '10px',
          backgroundColor: colors.factoryGrayVeryLight,
          borderRadius: '5px',
          backOverlay: true,
          backOverlayColor: 'rgba(0,0,0,0.5)',
          rtl: false,
          fontFamily: 'Poppins',
          cssAnimation: true,
          cssAnimationDuration: 300,
          cssAnimationStyle: 'fade',
          plainText: false,
          titleColor: colors.factoryBlue,
          titleFontSize: '16px',
          titleMaxLength: 34,
          messageColor: colors.factoryDark,
          messageFontSize: '14px',
          messageMaxLength: 99999,
          buttonsFontSize: '15px',
          buttonsMaxLength: 34,
          okButtonColor: colors.factoryGrayVeryLight,
          okButtonBackground: colors.factoryBlue,
          cancelButtonColor: colors.factoryBlue,
          cancelButtonBackground: colors.factoryGrayVeryLight,
        })

        setIsNotiflixReady(true)
      } catch (error) {
        setIsNotiflixReady(true) // Set to true anyway to prevent blocking
      }
    }

    initializeNotiflix()
  }, [])

  // Context value with all functions
  const contextValue: IContextProps = React.useMemo(
    () => ({
      showLoader: (ref?: any) => {
        try {
          if (!ref?.current?.className) return
          const className = ref.current.className
          const element = document.querySelector(
            `.${className.replace(/ /g, '.')}`,
          )
          if (element && isNotiflixReady) {
            Block.circle(`.${className.replace(/ /g, '.')}`)
          }
        } catch (error) {
          console.error('showLoader error:', error)
        }
      },

      hideLoader: (ref?: any) => {
        try {
          if (!ref?.current?.className) return
          const className = ref.current.className
          const element = document.querySelector(
            `.${className.replace(/ /g, '.')}`,
          )
          if (element && isNotiflixReady) {
            Block.remove(`.${className.replace(/ /g, '.')}`)
          }
        } catch (error) {
          console.error('hideLoader error:', error)
        }
      },

      showPageLoader: () => {
        try {
          if (isNotiflixReady) {
            Loading.circle()
          } else {
            console.log('⏳ showPageLoader called before Notiflix ready')
          }
        } catch (error) {
          console.error('showPageLoader error:', error)
        }
      },

      hidePageLoader: () => {
        try {
          if (isNotiflixReady) {
            Loading.remove()
          } else {
            console.log('⏳ hidePageLoader called before Notiflix ready')
          }
        } catch (error) {
          console.error('hidePageLoader error:', error)
        }
      },

      showSuccess: (message = '') => {
        try {
          if (isNotiflixReady) {
            Notify.success(message)
          } else {
            console.log('✅ Success (Notiflix not ready):', message)
          }
        } catch (error) {
          console.error('showSuccess error:', error)
          console.log('✅ Success (fallback):', message)
        }
      },

      showFailure: (message = '') => {
        try {
          if (isNotiflixReady) {
            Notify.failure(message)
          } else {
            console.error('❌ Error (Notiflix not ready):', message)
          }
        } catch (error) {
          console.error('showFailure error:', error)
          console.error('❌ Error (fallback):', message)
        }
      },

      showInfo: (message = '') => {
        try {
          if (isNotiflixReady) {
            Notify.info(message)
          } else {
            console.log('ℹ️ Info (Notiflix not ready):', message)
          }
        } catch (error) {
          console.error('showInfo error:', error)
          console.log('ℹ️ Info (fallback):', message)
        }
      },

      showWarning: (message = '') => {
        try {
          if (isNotiflixReady) {
            Notify.warning(message)
          } else {
            console.warn('⚠️ Warning (Notiflix not ready):', message)
          }
        } catch (error) {
          console.error('showWarning error:', error)
          console.warn('⚠️ Warning (fallback):', message)
        }
      },

      removeNotification: () => {
        try {
          if (isNotiflixReady) {
            Notify.remove()
          }
        } catch (error) {
          console.error('removeNotification error:', error)
        }
      },

      pixelConfirm: (opt: any) => {
        try {
          if (isNotiflixReady) {
            Confirm.show(
              opt?.title || '',
              opt?.message || '',
              'Yes',
              'No',
              () => opt?.onConfirm && opt.onConfirm(),
              () => opt?.onDecline && opt.onDecline(),
              {},
            )
          } else {
            console.log(
              '🔔 pixelConfirm using browser confirm (Notiflix not ready)',
            )
            const result = window.confirm(opt?.message || 'Are you sure?')
            if (result && opt?.onConfirm) opt.onConfirm()
            else if (!result && opt?.onDecline) opt.onDecline()
          }
        } catch (error) {
          console.error('pixelConfirm error:', error)
          const result = window.confirm(opt?.message || 'Are you sure?')
          if (result && opt?.onConfirm) opt.onConfirm()
          else if (!result && opt?.onDecline) opt.onDecline()
        }
      },

      pixelPrompt: (opt: any) => {
        try {
          if (isNotiflixReady) {
            Confirm.prompt(
              opt?.title || '',
              opt?.message || '',
              '',
              'Yes',
              'No',
              (value: any) => opt?.onConfirm && opt.onConfirm(value),
              (value: any) => opt?.onDecline && opt.onDecline(value),
              {},
            )
          } else {
            console.log(
              '🔔 pixelPrompt using browser prompt (Notiflix not ready)',
            )
            const result = window.prompt(opt?.message || 'Enter value:')
            if (result !== null && opt?.onConfirm) opt.onConfirm(result)
            else if (result === null && opt?.onDecline) opt.onDecline(result)
          }
        } catch (error) {
          console.error('pixelPrompt error:', error)
          const result = window.prompt(opt?.message || 'Enter value:')
          if (result !== null && opt?.onConfirm) opt.onConfirm(result)
          else if (result === null && opt?.onDecline) opt.onDecline(result)
        }
      },

      bannerInfo,

      showPixelBanner: (banner: IBannerInfo) => {
        setBannerInfo(banner)
      },
    }),
    [bannerInfo, isNotiflixReady],
  )

  return (
    <PixelFactoryContext.Provider value={contextValue}>
      {children}
    </PixelFactoryContext.Provider>
  )
}

// Custom hook for easier usage
export const usePixelFactory = () => {
  const context = useContext(PixelFactoryContext)
  if (!context) {
    throw new Error(
      'usePixelFactory must be used within a PixelFactory provider',
    )
  }
  return context
}

// Export both named and default exports for flexibility
export default PixelFactory
